
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Trash2, Eye, Ban, Unlock, Zap } from 'lucide-react';
import { Bike } from '@/services/types';
import { BikeStatusIndicator } from '@/components/bikes/BikeStatusIndicator';

interface BikeTableColumnsProps {
  onEditClick: (bike: Bike) => void;
  onDeleteClick: (bike: Bike) => void;
  onViewRentalsClick: (bike: Bike) => void;
  onToggleMovementBlock: (bike: Bike) => void;
  onOpenSpeedLimitDialog: (bike: Bike) => void;
}

export const getBikeColumns = ({
  onEditClick,
  onDeleteClick,
  onViewRentalsClick,
  onToggleMovementBlock,
  onOpenSpeedLimitDialog
}: BikeTableColumnsProps) => [
  {
    key: 'serialNumber',
    header: 'Серийный номер',
    cell: (bike: Bike) => bike.serialNumber || 'Не указан',
    sortable: true,
  },
  {
    key: 'gps_tag',
    header: 'IoT',
    cell: (bike: Bike) => {
      if (!bike.gps_tag) {
        return <span className="text-muted-foreground">Не указан</span>;
      }
      return (
        <Badge variant="outline" className="font-mono">
          {bike.gps_tag}
        </Badge>
      );
    },
    sortable: true,
  },
  {
    key: 'model',
    header: 'Модель',
    cell: (bike: Bike) => bike.model || 'Не указана',
    sortable: true,
  },
  {
    key: 'status',
    header: 'Статус',
    cell: (bike: Bike) => {
      const statusMap = {
        available: { label: 'Доступен', variant: 'default' as const },
        in_use: { label: 'В работе', variant: 'secondary' as const },
        maintenance: { label: 'На обслуживании', variant: 'outline' as const },
        retired: { label: 'Списан', variant: 'secondary' as const },
      };
      
      const status = statusMap[bike.status as keyof typeof statusMap] || statusMap.available;
      
      return <Badge variant={status.variant}>{status.label}</Badge>;
    },
    sortable: true,
  },
  {
    key: 'movement_status',
    header: 'Управление движением',
    cell: (bike: Bike) => <BikeStatusIndicator bike={bike} />,
    sortable: false,
  },
  {
    key: 'courierName',
    header: 'Курьер',
    cell: (bike: Bike) => {
      if (bike.courierName) {
        return (
          <div className="flex flex-col">
            <span className="font-medium">{bike.courierName}</span>
            {bike.rental_human_number && (
              <span className="text-xs text-muted-foreground">
                Аренда: {bike.rental_human_number}
              </span>
            )}
          </div>
        );
      }
      return <span className="text-muted-foreground">Не назначен</span>;
    },
    sortable: true,
  },
  {
    key: 'mileage',
    header: 'Пробег (км)',
    cell: (bike: Bike) => bike.mileage || '0',
    sortable: true,
  },
  {
    key: 'lastMaintenance',
    header: 'Последнее ТО',
    cell: (bike: Bike) => {
      if (!bike.lastMaintenance) return 'Не проводилось';
      return new Date(bike.lastMaintenance).toLocaleDateString('ru-RU');
    },
    sortable: true,
  },
  {
    key: 'actions',
    header: 'Действия',
    cell: (bike: Bike) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Открыть меню</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEditClick(bike)}>
            <Edit className="mr-2 h-4 w-4" />
            Редактировать
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onViewRentalsClick(bike)}>
            <Eye className="mr-2 h-4 w-4" />
            История аренд
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onToggleMovementBlock(bike)}>
            {bike.movement_blocked ? (
              <>
                <Unlock className="mr-2 h-4 w-4" />
                Разблокировать
              </>
            ) : (
              <>
                <Ban className="mr-2 h-4 w-4" />
                Заблокировать
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onOpenSpeedLimitDialog(bike)}>
            <Zap className="mr-2 h-4 w-4" />
            Ограничить скорость
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => onDeleteClick(bike)}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Удалить
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
    sortable: false,
  },
];
